import { API_ROOT_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  ActionCommentListResponse,
  ActionHistoryListResponse,
  ArchiveActionsPayload,
  ArchiveFindingsPayload,
  AuditsForFilterResponse,
  BulkUpdateActionAssigneePayload,
  BulkUpdateActionCollaboratorPayload,
  BulkUpdateActionImpactAreaPayload,
  BulkUpdateActionPriorityPayload,
  BulkUpdateActionReporterPayload,
  BulkUpdateActionTagsPayload,
  CapaActionDetailResponse,
  CapaActionListResponse,
  CapaFilterSourcesResponse,
  CapaFindingDetailResponse,
  CapaFindingListResponse,
  CapaPlanDetailResponse,
  CapaPlanListResponse,
  CreateFindingPayload,
  CreateFindingResponse,
  CreatePlanPayload,
  CreatePlanResponse,
  EntityTag,
  EntityTagListResponse,
  ExportFindingsPayload,
  ImpactAreasResponse,
  LinkActionsPayload,
  LinkEntityTagsPayload,
  LinkWatchersPayload,
  QuickAddActionPayload,
  RemediationStatus,
  SlcpAssessmentsForFilterResponse,
  SuggestedFindingWithActions,
  UnarchiveActionsPayload,
  UnarchiveFindingsPayload,
  UpdateRemediationPayload,
} from './types';

/**
 * Builds the `?updateLinkedActions=` suffix for the endpoints that can push a
 * plan change down to its actions. Empty string when the caller has no answer to
 * send, so those requests stay byte-identical to before.
 */
function updateLinkedActionsQuery(updateLinkedActions?: boolean): string {
  return updateLinkedActions === undefined
    ? ''
    : `?updateLinkedActions=${updateLinkedActions}`;
}

export function fetchMyCompanyFindings(
  qs: string,
): Promise<CapaFindingListResponse> {
  return apiFetch<CapaFindingListResponse>(
    `/capa/findings/my-company?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function fetchNetworkFindings(
  qs: string,
): Promise<CapaFindingListResponse> {
  return apiFetch<CapaFindingListResponse>(
    `/capa/findings/network?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function fetchMyCompanyArchive(
  qs: string,
): Promise<CapaFindingListResponse> {
  return apiFetch<CapaFindingListResponse>(
    `/capa/findings/my-company/archive?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function fetchNetworkArchive(
  qs: string,
): Promise<CapaFindingListResponse> {
  return apiFetch<CapaFindingListResponse>(
    `/capa/findings/network/archive?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function patchFinding(
  id: string,
  payload: Record<string, unknown>,
): Promise<void> {
  return apiFetch<void>(
    `/capa/findings/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function archiveFindings(
  payload: ArchiveFindingsPayload,
): Promise<void> {
  return apiFetch<void>(
    '/capa/findings/archive',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

/**
 * The impact-area taxonomy — reference data, cached for a day by
 * `useImpactAreasQuery`.
 *
 * Served by `modules/companies` on the platform API, not the legacy
 * api-service, so it needs `API_ROOT_URL` explicitly. It replaced
 * `/v3/impact-areas?isDropDown=true`, whose ids the database no longer holds.
 */
export function fetchImpactAreas(): Promise<ImpactAreasResponse> {
  return apiFetch<ImpactAreasResponse>('/api/impact-areas', {}, API_ROOT_URL);
}

export function fetchEntityTags(): Promise<EntityTagListResponse> {
  return apiFetch<EntityTagListResponse>(
    '/entity-tags?count=100',
    {},
    BACKEND_URL,
  );
}

export function linkEntityTags(payload: LinkEntityTagsPayload): Promise<void> {
  return apiFetch<void>(
    '/entity-tags/link',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function unarchiveFindings(
  payload: UnarchiveFindingsPayload,
): Promise<void> {
  return apiFetch<void>(
    '/capa/findings/unarchive',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function fetchFinding(id: string): Promise<CapaFindingDetailResponse> {
  return apiFetch<CapaFindingDetailResponse>(
    `/capa/findings/details/${id}`,
    {},
    BACKEND_URL,
  );
}

export function fetchFindingActions(
  qs: string,
): Promise<CapaActionListResponse> {
  return apiFetch<CapaActionListResponse>(
    `/capa/actions?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function fetchFindingPlans(qs: string): Promise<CapaPlanListResponse> {
  return apiFetch<CapaPlanListResponse>(`/capa/caps?${qs}`, {}, BACKEND_URL);
}

export function fetchCapsForFilter(): Promise<CapaPlanListResponse> {
  // page is 0-indexed on this endpoint; `page=1` returned the (empty) second
  // page. quickFilter=all mirrors the working action-detail caps query so the
  // full list is returned regardless of assignment.
  return apiFetch<CapaPlanListResponse>(
    '/capa/caps?sort=title%3AASC&count=100&page=0&quickFilter=all',
    {},
    BACKEND_URL,
  );
}

export function fetchCapsForAction(qs: string): Promise<CapaPlanListResponse> {
  return apiFetch<CapaPlanListResponse>(`/capa/caps?${qs}`, {}, BACKEND_URL);
}

export function quickAddAction(payload: QuickAddActionPayload): Promise<void> {
  return apiFetch<void>(
    '/capa/actions/quick-add-actions',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function createEntityTag(payload: {
  title: string;
  colorCode: string;
  description?: string;
}): Promise<{ data: EntityTag }> {
  return apiFetch<{ data: EntityTag }>(
    '/entity-tags',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function updateEntityTag(
  id: string,
  payload: { title: string; colorCode: string; description?: string },
): Promise<void> {
  return apiFetch<void>(
    `/entity-tags/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function deleteEntityTag(id: string): Promise<void> {
  return apiFetch<void>(
    `/entity-tags/${id}`,
    { method: 'DELETE' },
    BACKEND_URL,
  );
}

export function fetchPlansFilterSources(): Promise<CapaFilterSourcesResponse> {
  return apiFetch<CapaFilterSourcesResponse>(
    '/capa/caps/filter-sources',
    {},
    BACKEND_URL,
  );
}

export function fetchActionsFilterSources(): Promise<CapaFilterSourcesResponse> {
  return apiFetch<CapaFilterSourcesResponse>(
    '/capa/actions/filter-sources',
    {},
    BACKEND_URL,
  );
}

// Both audits pickers ride the migrated audits list (`GET /api/audits`,
// legacy `{ data, meta }` envelope preserved).
export function fetchAuditsForFilter(): Promise<AuditsForFilterResponse> {
  return apiFetch<AuditsForFilterResponse>(
    '/api/audits?sort=name:ASC&count=200',
    {},
    API_ROOT_URL,
  );
}

export function fetchAuditsForCompany(
  qs: string,
): Promise<AuditsForFilterResponse> {
  return apiFetch<AuditsForFilterResponse>(
    `/api/audits?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export function fetchSlcpAssessmentsForFilter(): Promise<SlcpAssessmentsForFilterResponse> {
  return apiFetch<SlcpAssessmentsForFilterResponse>(
    '/slcp/assessments?isArchived=false&count=200',
    {},
    BACKEND_URL,
  );
}

export function fetchSlcpAssessmentsForCompany(
  qs: string,
): Promise<SlcpAssessmentsForFilterResponse> {
  return apiFetch<SlcpAssessmentsForFilterResponse>(
    `/slcp/assessments?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function createFinding(
  payload: CreateFindingPayload,
): Promise<CreateFindingResponse> {
  return apiFetch<CreateFindingResponse>(
    '/capa/findings',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function exportFindings(payload: ExportFindingsPayload): Promise<void> {
  return apiFetch<void>(
    '/capa/export',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function fetchActionsActive(
  qs: string,
): Promise<CapaActionListResponse> {
  return apiFetch<CapaActionListResponse>(
    `/capa/actions?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function fetchActionsArchive(
  qs: string,
): Promise<CapaActionListResponse> {
  return apiFetch<CapaActionListResponse>(
    `/capa/actions/archive?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function patchAction(
  id: string,
  payload: Record<string, unknown>,
): Promise<void> {
  return apiFetch<void>(
    `/capa/actions/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function archiveActions(payload: ArchiveActionsPayload): Promise<void> {
  return apiFetch<void>(
    '/capa/archive',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function unarchiveActions(
  payload: UnarchiveActionsPayload,
): Promise<void> {
  return apiFetch<void>(
    '/capa/unarchive',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function deleteActions(payload: ArchiveActionsPayload): Promise<void> {
  return apiFetch<void>(
    '/capa/delete',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function bulkUpdateActionPriority(
  body: BulkUpdateActionPriorityPayload,
): Promise<void> {
  return apiFetch<void>(
    '/capa/actions/bulk-severity',
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
    BACKEND_URL,
  );
}

export function bulkUpdateActionImpactArea(
  body: BulkUpdateActionImpactAreaPayload,
): Promise<void> {
  return apiFetch<void>(
    '/capa/actions/bulk-impact-area',
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
    BACKEND_URL,
  );
}

export function bulkUpdateActionTags(
  body: BulkUpdateActionTagsPayload,
): Promise<void> {
  return apiFetch<void>(
    '/capa/actions/bulk-tags',
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
    BACKEND_URL,
  );
}

export function bulkUpdateActionAssignee(
  body: BulkUpdateActionAssigneePayload,
): Promise<void> {
  return apiFetch<void>(
    '/capa/actions/bulk-assignee',
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
    BACKEND_URL,
  );
}

export function bulkUpdateActionReporter(
  body: BulkUpdateActionReporterPayload,
): Promise<void> {
  return apiFetch<void>(
    '/capa/actions/bulk-reporter',
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
    BACKEND_URL,
  );
}

export function bulkUpdateActionCollaborator(
  body: BulkUpdateActionCollaboratorPayload,
): Promise<void> {
  return apiFetch<void>(
    '/capa/actions/bulk-collaborator',
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
    BACKEND_URL,
  );
}

export function fetchPlansActive(qs: string): Promise<CapaPlanListResponse> {
  return apiFetch<CapaPlanListResponse>(`/capa/caps?${qs}`, {}, BACKEND_URL);
}

export function fetchPlansArchive(qs: string): Promise<CapaPlanListResponse> {
  return apiFetch<CapaPlanListResponse>(
    `/capa/caps/archive?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function createPlan(
  payload: CreatePlanPayload,
): Promise<CreatePlanResponse> {
  return apiFetch<CreatePlanResponse>(
    '/capa/caps',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export function archivePlans(
  payload: ArchiveActionsPayload,
  includeLinkedActions: boolean,
): Promise<void> {
  return apiFetch<void>(
    `/capa/archive?includeLinkedActions=${includeLinkedActions}`,
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export function unarchivePlans(
  payload: UnarchiveActionsPayload,
): Promise<void> {
  return apiFetch<void>(
    '/capa/unarchive',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export function deletePlans(
  payload: ArchiveActionsPayload,
  includeLinkedActions: boolean,
): Promise<void> {
  return apiFetch<void>(
    `/capa/delete?includeLinkedActions=${includeLinkedActions}`,
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export function fetchActionDetail(
  id: string,
): Promise<CapaActionDetailResponse> {
  return apiFetch<CapaActionDetailResponse>(
    `/capa/actions/${id}`,
    {},
    BACKEND_URL,
  );
}

export function fetchPlanDetail(id: string): Promise<CapaPlanDetailResponse> {
  return apiFetch<CapaPlanDetailResponse>(`/capa/caps/${id}`, {}, BACKEND_URL);
}

export function patchPlan(
  id: string,
  payload: Record<string, unknown>,
  updateLinkedActions?: boolean,
): Promise<CapaPlanDetailResponse> {
  return apiFetch<CapaPlanDetailResponse>(
    `/capa/caps/${id}${updateLinkedActionsQuery(updateLinkedActions)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

// Upsert: creates the remediation request, or overwrites the existing one for
// this action. `attachmentIds` is the complete set — anything omitted is
// detached. `status` defaults to PENDING server-side, i.e. saved-not-submitted.
export function createRemediationRequest(
  actionId: string,
  payload: {
    message: string;
    attachmentIds: string[];
    status?: RemediationStatus;
  },
): Promise<void> {
  return apiFetch<void>(
    `/capa/actions/${actionId}/remediation-requests`,
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export function updateRemediationRequest(
  actionId: string,
  payload: UpdateRemediationPayload,
): Promise<void> {
  return apiFetch<void>(
    `/capa/actions/${actionId}/remediation-requests`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

// Returns a single zip bundling every file on the remediation request; the
// hook triggers the browser download from `data.url`.
export function downloadRemediationFiles(
  actionId: string,
): Promise<{ data: { id: string; url: string } }> {
  return apiFetch<{ data: { id: string; url: string } }>(
    `/capa/actions/${actionId}/remediation-requests/download-files`,
    {},
    BACKEND_URL,
  );
}

export function fetchActionComments(
  actionId: string,
  qs: string,
): Promise<ActionCommentListResponse> {
  return apiFetch<ActionCommentListResponse>(
    `/capa/actions/${actionId}/comments?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function fetchActionHistories(
  actionId: string,
  qs: string,
): Promise<ActionHistoryListResponse> {
  return apiFetch<ActionHistoryListResponse>(
    `/capa/actions/${actionId}/histories?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function postActionComment(
  actionId: string,
  payload: { content: string; visibility: 'PUBLIC' | 'PRIVATE' | 'ADMINNOTE' },
): Promise<void> {
  return apiFetch<void>(
    `/capa/actions/${actionId}/comments`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function patchActionComment(
  actionId: string,
  commentId: string,
  payload: { content: string },
): Promise<void> {
  return apiFetch<void>(
    `/capa/actions/${actionId}/comments/${commentId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
    BACKEND_URL,
  );
}

export function fetchPlanHistories(
  planId: string,
  qs: string,
): Promise<ActionHistoryListResponse> {
  return apiFetch<ActionHistoryListResponse>(
    `/capa/caps/${planId}/histories?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function linkWatchers(payload: LinkWatchersPayload): Promise<void> {
  return apiFetch<void>(
    '/capa/link-users',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export function linkCapaActions(
  id: string,
  payload: LinkActionsPayload,
): Promise<{ data: { linkedCount: number } }> {
  return apiFetch<{ data: { linkedCount: number } }>(
    `/capa/actions/${id}/link`,
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export function unlinkCapaActions(
  id: string,
  payload: LinkActionsPayload,
): Promise<void> {
  return apiFetch<void>(
    `/capa/actions/${id}/unlink`,
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export function bulkLinkActionsToPlan(
  items: { id: string; capId: string }[],
  updateLinkedActions?: boolean,
): Promise<{ data: { updatedCount: number; message: string } }> {
  return apiFetch<{ data: { updatedCount: number; message: string } }>(
    `/capa/actions/bulk-link-cap${updateLinkedActionsQuery(updateLinkedActions)}`,
    { method: 'PATCH', body: JSON.stringify(items) },
    BACKEND_URL,
  );
}

export function fetchSuggestedActions(
  planId: string,
): Promise<{ data: { findings: SuggestedFindingWithActions[] } }> {
  return apiFetch<{ data: { findings: SuggestedFindingWithActions[] } }>(
    `/capa/caps/${planId}/suggested-actions`,
    {},
    BACKEND_URL,
  );
}
